import React, { useState, useRef, useEffect } from 'react';

/**
 * TruncatedText component that truncates long text and shows full content on hover
 * @param {Object} props
 * @param {string} props.text - The text to display
 * @param {number} props.maxLength - Maximum characters to show before truncation (default: 100)
 * @param {string} props.className - Additional CSS classes
 * @param {boolean} props.showTooltip - Whether to show tooltip on hover (default: true)
 */
export default function TruncatedText({ 
  text, 
  maxLength = 100, 
  className = '', 
  showTooltip = true 
}) {
  const [showTooltipState, setShowTooltipState] = useState(false);
  const [tooltipPosition, setTooltipPosition] = useState({ x: 0, y: 0 });
  const textRef = useRef(null);

  // Check if text needs truncation
  const needsTruncation = text && text.length > maxLength;
  const displayText = needsTruncation ? text.substring(0, maxLength) + '...' : text;

  // Handle mouse enter
  const handleMouseEnter = (e) => {
    if (!showTooltip || !needsTruncation) return;
    
    const rect = e.currentTarget.getBoundingClientRect();
    setTooltipPosition({
      x: rect.left + rect.width / 2,
      y: rect.top - 10
    });
    setShowTooltipState(true);
  };

  // Handle mouse leave
  const handleMouseLeave = () => {
    setShowTooltipState(false);
  };

  // Handle mouse move to keep tooltip following cursor
  const handleMouseMove = (e) => {
    if (!showTooltipState) return;
    
    setTooltipPosition({
      x: e.clientX,
      y: e.clientY - 10
    });
  };

  // Hide tooltip on scroll
  useEffect(() => {
    const handleScroll = () => setShowTooltipState(false);
    window.addEventListener('scroll', handleScroll, true);
    return () => window.removeEventListener('scroll', handleScroll, true);
  }, []);

  if (!text) {
    return <span className={className}>—</span>;
  }

  return (
    <>
      <span
        ref={textRef}
        className={`cursor-pointer ${className}`}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        onMouseMove={handleMouseMove}
      >
        {displayText}
      </span>
      
      {showTooltipState && needsTruncation && (
        <div
          className="fixed z-50 max-w-md p-3 bg-gray-900 text-white text-sm rounded-lg shadow-lg pointer-events-none"
          style={{
            left: tooltipPosition.x,
            top: tooltipPosition.y,
            transform: 'translateX(-50%) translateY(-100%)',
          }}
        >
          <div className="whitespace-pre-wrap break-words">
            {text}
          </div>
          {/* Tooltip arrow */}
          <div 
            className="absolute top-full left-1/2 transform -translate-x-1/2 w-0 h-0 border-l-4 border-r-4 border-t-4 border-transparent border-t-gray-900"
          />
        </div>
      )}
    </>
  );
}
